import { FC } from 'react';
import { Box, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { TooltipCard } from '@components/Tooltip';
import { useDirection } from '@hooks';
import { useTrans } from '@libs/i18n';
import { IconTelescope } from '@tabler/icons-react';

import { CHAT_TOKENS } from '../constants';

export interface DeepResearchToggleProps {
  enabled: boolean;
  onToggle: () => void;
  disabled?: boolean;
}

/**
 * Deep Research pill for the composer.
 *
 * Off it reads as a quiet outlined chip; on it fills with the brand
 * blue→green wash, gains a state dot and sweeps once so the mode change is
 * felt rather than just read.
 */
export const DeepResearchToggle: FC<DeepResearchToggleProps> = observer(
  ({ enabled, onToggle, disabled = false }) => {
    const { t } = useTrans();
    const theme = useTheme();
    const { isRtl } = useDirection();

    return (
      <TooltipCard
        variant="dark"
        placement="top"
        maxWidth={214}
        description={t('chat.input.deepResearchHint') as string}
      >
        <Box
          component="button"
          type="button"
          aria-pressed={enabled}
          aria-label={t('chat.input.deepResearch') as string}
          onClick={onToggle}
          disabled={disabled}
          sx={{
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center',
            flexShrink: 0,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            // Collapsed it is exactly the icon's 38px circle — matching the attach
            // button — and widens to fit the label only while hovered or focused.
            height: 38,
            p: 0,
            borderRadius: '999px',
            border: '1px solid',
            borderColor: enabled ? '#B9D6FA' : CHAT_TOKENS.hairline,
            background: enabled ? 'linear-gradient(100deg,#EAF2FE,#E8F6EF)' : '#fff',
            color: enabled ? CHAT_TOKENS.deepInk : 'text.secondary',
            fontFamily: theme.typography.fontFamily,
            fontSize: '0.719rem',
            fontWeight: enabled ? 600 : 400,
            lineHeight: 1,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1,
            outline: 'none',
            boxShadow: enabled ? '0 1px 6px rgba(33,120,239,.14)' : 'none',
            transition: theme.transitions.create(
              ['background', 'color', 'border-color', 'transform', 'box-shadow'],
              { duration: 220 },
            ),
            '&:hover': disabled
              ? {}
              : {
                  borderColor: enabled ? '#B9D6FA' : '#CFE0F7',
                  color: enabled ? CHAT_TOKENS.deepInk : 'text.primary',
                  boxShadow: enabled ? '0 3px 12px rgba(33,120,239,.2)' : 'none',
                },
            '&:active': disabled ? {} : { transform: 'scale(.96)' },
            '&:focus-visible': {
              outline: `2px solid ${theme.palette.accent.blue}`,
              outlineOffset: 2,
            },
            // The label is the only thing that expands, so the pill's width is
            // always exactly what is on screen.
            '& .deep-reveal': {
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              maxWidth: 0,
              opacity: 0,
              overflow: 'hidden',
              marginInlineEnd: 0,
              transition:
                'max-width .26s cubic-bezier(.2,.9,.25,1), opacity .18s ease, margin-inline-end .26s cubic-bezier(.2,.9,.25,1)',
            },
            ...(disabled
              ? {}
              : {
                  '&:hover .deep-reveal, &:focus-visible .deep-reveal': {
                    maxWidth: 200,
                    opacity: 1,
                    marginInlineEnd: '14px',
                  },
                }),
            // One-shot light sweep across the pill as it switches on.
            '@keyframes deepResearchSweep': {
              to: { transform: 'translateX(120%)' },
            },
            '&::after': enabled
              ? {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  background:
                    'linear-gradient(100deg,transparent 20%,rgba(255,255,255,.75) 50%,transparent 80%)',
                  transform: 'translateX(-120%)',
                  pointerEvents: 'none',
                  animation: 'deepResearchSweep .7s ease-out',
                }
              : {},
            '@media (prefers-reduced-motion: reduce)': {
              '&::after': { animation: 'none' },
            },
          }}
        >
          {/* Always-visible face of the control — the collapsed pill is this circle. */}
          <Box
            component="span"
            sx={{
              // 36 + the pill's 1px borders == the 38px control height, so the
              // collapsed state is a true circle.
              width: 36,
              height: 36,
              flexShrink: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IconTelescope
              size={16}
              stroke={2}
              style={{
                flexShrink: 0,
                color: enabled ? theme.palette.accent.blue : 'inherit',
                transform: `${isRtl ? 'scaleX(-1) ' : ''}${enabled ? 'scale(1.05)' : ''}`,
                transition: 'transform .35s cubic-bezier(.34,1.56,.64,1)',
              }}
            />
          </Box>

          <Box component="span" className="deep-reveal">
            {t('chat.input.deepResearchLabel')}
            {enabled && (
              <Box
                component="span"
                sx={{
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  backgroundColor: 'accent.blue',
                  flexShrink: 0,
                }}
              />
            )}
          </Box>
        </Box>
      </TooltipCard>
    );
  },
);
